import React, { useCallback, useEffect, useState } from "react";
import baseUrl from "../../../../api/baseUrl";
import { useNavigate, useParams } from "react-router-dom";
import getDecryptedToken from "../../../../api/getDecryptedToken";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { toast } from "react-toastify";
import { patienttitles } from "../../../../utils/title";
import { Autocomplete, Chip, TextField } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PaletteIcon from "@mui/icons-material/Palette";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import SchoolIcon from "@mui/icons-material/School";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import TranslateIcon from "@mui/icons-material/Translate";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import type { EmployeeFormState } from "./employeeFormShared";
import {
  colorOptions,
  languageOptions,
  normalizeEmployeeCalendarColor,
} from "./employeeFormShared";

interface Role {
  _id: string;
  roleName: string;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
}

const emptyForm: EmployeeFormState = {
  firstName: "",
  title: "",
  lastName: "",
  email: "",
  password: "",
  confirmpassword: "",
  roleId: "",
  phoneNumber: "",
  address: "",
  employeeId: "",
  color: "",
  imageUrl: "",
  IsDentist: false,
  IsBookDentist: false,
  image: null,
  studies: [],
  specialities: [],
  languages: [],
};

const EditEmployee: React.FC = () => {
  const navigate = useNavigate();
  const { ID } = useParams<{ ID: string }>();
  const [formData, setFormData] = useState<EmployeeFormState>(emptyForm);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [emailExistSameClinic, setEmailExistSameClinic] = useState(false);

  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [updatePassword, setUpdatePassword] = useState("");
  const [updateConfirmPassword, setUpdateConfirmPassword] = useState("");
  const [currentUserPassword, setCurrentUserPassword] = useState("");
  const [updatePasswordNotMatch, setUpdatePasswordNotMatch] = useState(false);
  const [updatePwdLength, setUpdatePwdLength] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);
  const [showUpdateConfirmPassword, setShowUpdateConfirmPassword] = useState(false);

  const [savingPhoto, setSavingPhoto] = useState(false);
  const [savingBasic, setSavingBasic] = useState(false);
  const [savingColor, setSavingColor] = useState(false);
  const [savingProf, setSavingProf] = useState(false);
  const [savingType, setSavingType] = useState(false);
  const [isLastActiveSuperAdmin, setIsLastActiveSuperAdmin] = useState(false);

  const roleName = getDecryptedToken("role");
  const currentClinicId = getDecryptedToken("cid");

  const loadEmployee = useCallback(async () => {
    if (!ID) return;
    const employeeResponse = await baseUrl.get("/employee/findEmployeeById", {
      headers: { employeeid: ID },
    });
    const data = employeeResponse.data.data;
    const roleId =
      typeof data.roleId === "object" && data.roleId !== null
        ? (data.roleId as Role)._id
        : (data.roleId as string) ?? "";
    setFormData((prev) => ({
      ...prev,
      ...data,
      roleId,
      color: normalizeEmployeeCalendarColor(data.color) ?? data.color ?? "",
    }));
    setIsLastActiveSuperAdmin(Boolean(data.isLastActiveSuperAdmin));
    if (data.imageUrl) {
      setImagePreview(data.imageUrl);
    }
  }, [ID]);

  useEffect(() => {
    if (!ID) {
      navigate("/client/staff/employee/all-employees", { replace: true });
      return;
    }
    const run = async () => {
      try {
        setLoadingPage(true);
        const rolesResponse = await baseUrl.get("/role/findRole", {
          headers: { status: true },
        });
        setRoles(rolesResponse.data.data);
        await loadEmployee();
        setEmailExistSameClinic(false);
      } catch {
        toast.error("Failed to load employee");
      } finally {
        setLoadingPage(false);
      }
    };
    run();
  }, [ID, navigate, loadEmployee]);

  const patchEmployee = async (
    endpoint:
      | "updateEmployeeProfilePhoto"
      | "updateEmployeeBasicInfo"
      | "updateEmployeeCalendarColor"
      | "updateEmployeeProfessionalDetails"
      | "updateEmployeeType",
    payload: Record<string, unknown>
  ) => {
    await baseUrl.patch(`/employee/${endpoint}`, payload, {
      headers: { employeeid: ID },
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;
    const checked = target.checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (type === "file" && target.files?.[0]) {
      const file = target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.readyState === 2) {
          setFormData((prev) => ({ ...prev, image: file }));
          setImagePreview(reader.result as string);
        }
      };
      reader.readAsDataURL(file);
    }

    if (name === "email") {
      const emailValue = value.trim();
      if (emailValue) {
        baseUrl
          .get("/employee/findEmployeeByEmail", {
            headers: { email: emailValue, employeeid: ID },
          })
          .then((res) => {
            const data = res.data.data || [];
            const sameClinicEmployees = data.filter(
              (item: { clinicId?: string }) => item.clinicId === currentClinicId
            );
            setEmailExistSameClinic(sameClinicEmployees.length > 0);
          })
          .catch(() => setEmailExistSameClinic(false));
      } else {
        setEmailExistSameClinic(false);
      }
    }
  };

  const handlePhoneNumberChange = (value: string | undefined) => {
    setFormData((prev) => ({ ...prev, phoneNumber: value || "" }));
  };

  const handleLanguageToggle = (lang: string) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter((l) => l !== lang)
        : [...prev.languages, lang],
    }));
  };

  const handleUpdatePhoto = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.IsBookDentist) {
      toast.warning("Enable Online Booking Dentist to update profile photo.");
      return;
    }
    if (!(formData.image instanceof File)) {
      toast.info("Choose a new image file first.");
      return;
    }
    try {
      setSavingPhoto(true);
      const image = await fileToBase64(formData.image);
      await patchEmployee("updateEmployeeProfilePhoto", { image });
      toast.success("Profile photo updated");
      setFormData((prev) => ({ ...prev, image: null }));
      await loadEmployee();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || "Failed to update photo");
    } finally {
      setSavingPhoto(false);
    }
  };

  const handleUpdateBasic = async (e: React.FormEvent) => {
    e.preventDefault();
    if (emailExistSameClinic) {
      toast.error("This email is already used by another employee in this clinic.");
      return;
    }
    try {
      setSavingBasic(true);
      await patchEmployee("updateEmployeeBasicInfo", {
        email: formData.email.trim(),
        roleId: formData.roleId,
        title: formData.title,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phoneNumber: formData.phoneNumber,
        address: formData.address.trim(),
      });
      toast.success("Basic information updated");
      await loadEmployee();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || "Failed to update");
    } finally {
      setSavingBasic(false);
    }
  };

  const handleUpdateColor = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSavingColor(true);
      await patchEmployee("updateEmployeeCalendarColor", { color: formData.color });
      toast.success("Calendar color updated");
      await loadEmployee();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || "Failed to update color");
    } finally {
      setSavingColor(false);
    }
  };

  const handleUpdateProfessional = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.IsBookDentist) {
      toast.warning("Professional details apply to Online Booking Dentist.");
      return;
    }
    try {
      setSavingProf(true);
      await patchEmployee("updateEmployeeProfessionalDetails", {
        studies: formData.studies ?? [],
        specialities: formData.specialities ?? [],
        languages: formData.languages,
      });
      toast.success("Professional details updated");
      await loadEmployee();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || "Failed to update");
    } finally {
      setSavingProf(false);
    }
  };

  const handleUpdateEmployeeType = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSavingType(true);
      await patchEmployee("updateEmployeeType", {
        IsDentist: formData.IsDentist,
        IsBookDentist: formData.IsBookDentist,
      });
      toast.success("Employee type updated");
      await loadEmployee();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || "Failed to update");
    } finally {
      setSavingType(false);
    }
  };

  const handleUpdatePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "updatePassword") {
      setUpdatePassword(value);
      setUpdatePwdLength(value.length > 0 && value.length < 8);
      setUpdatePasswordNotMatch(value !== updateConfirmPassword);
    }
    if (name === "updateConfirmPassword") {
      setUpdateConfirmPassword(value);
      setUpdatePasswordNotMatch(updatePassword !== value);
    }
    if (name === "currentUserPassword") {
      setCurrentUserPassword(value);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUserPassword) {
      toast.error("Enter your current password.");
      return;
    }
    if (!updatePassword) {
      toast.error("Enter a new password.");
      return;
    }
    if (updatePwdLength) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (updatePasswordNotMatch) {
      toast.error("New passwords do not match.");
      return;
    }
    try {
      await baseUrl.patch(
        "/employee/updateEmployeePassword",
        {
          newPassword: updatePassword,
          currentUserPassword: currentUserPassword,
        },
        { headers: { employeeid: ID } }
      );
      toast.success("Password updated");
      setUpdatePassword("");
      setUpdateConfirmPassword("");
      setCurrentUserPassword("");
      setUpdatePasswordNotMatch(false);
      setUpdatePwdLength(false);
      setShowCurrentPassword(false);
      setShowUpdatePassword(false);
      setShowUpdateConfirmPassword(false);
    } catch (error: unknown) {
      const ax = error as { response?: { data?: { message?: string } } };
      toast.error(ax.response?.data?.message || "Failed to update password");
    }
  };

  if (loadingPage) {
    return (
      <div className="min-w-0 w-full p-6 text-center text-slate-600 dark:text-slate-300">
        Loading employee…
      </div>
    );
  }

  return (
    <div className="min-w-0 w-full overflow-x-hidden from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 p-3 bg-white dark:bg-slate-900">
      <div className="mx-auto max-w-full">
        <div className="mb-3 md:mb-4">
          <div className="flex items-center gap-2.5 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-primary/10 dark:bg-primary/20 rounded-lg md:rounded-xl flex items-center justify-center">
              <ManageAccountsIcon className="text-primary" sx={{ fontSize: { xs: 18, md: 20 } }} />
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-slate-800 dark:text-slate-100">
                Update Employee
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-xs md:text-sm mt-0.5">
                Save each section independently
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {/* Profile Photo */}
          <form
            onSubmit={handleUpdatePhoto}
            className="bg-white dark:bg-slate-800 rounded-[8px] shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-4 border border-slate-100 dark:border-slate-700"
          >
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <CameraAltIcon className="text-primary" sx={{ fontSize: 16 }} />
              Profile Photo
            </h2>
            {!formData.IsBookDentist ? (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Enable &quot;Online Booking Dentist&quot; in Employee Type to manage the booking profile photo.
              </p>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="relative group">
                    <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 border-2 border-white dark:border-slate-600 shadow-lg">
                      {imagePreview || formData.imageUrl ? (
                        <img
                          src={imagePreview || formData.imageUrl}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <PersonIcon className="text-slate-300 dark:text-slate-500" sx={{ fontSize: { xs: 40, md: 48 } }} />
                        </div>
                      )}
                    </div>
                    <label className="absolute bottom-0 right-0 w-8 h-8 bg-primary hover:bg-primaryDark rounded-full flex items-center justify-center cursor-pointer shadow-lg transition-all hover:scale-110">
                      <CameraAltIcon className="text-white" sx={{ fontSize: 16 }} />
                      <input
                        type="file"
                        name="image"
                        accept="image/*"
                        onChange={handleChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <div className="text-center sm:text-left flex-1">
                    <p className="text-slate-600 dark:text-slate-300 font-medium text-xs">Upload a new photo, then save this section.</p>
                    <p className="text-slate-400 dark:text-slate-500 text-[10px] mt-0.5">JPG, PNG or GIF</p>
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <button
                    type="submit"
                    disabled={savingPhoto}
                    className="px-4 py-2 rounded-lg bg-slate-900 dark:bg-primary text-white font-semibold text-xs disabled:opacity-50"
                  >
                    {savingPhoto ? "Saving…" : "Update photo"}
                  </button>
                </div>
              </>
            )}
          </form>

          {/* Basic Information */}
          <form
            onSubmit={handleUpdateBasic}
            className="bg-white dark:bg-slate-800 rounded-[8px] shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-4 border border-slate-100 dark:border-slate-700 min-w-0 overflow-hidden"
          >
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <PersonIcon className="text-primary" sx={{ fontSize: 16 }} />
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">
              <div className="space-y-1.5 min-w-0">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Role *</label>
                <div className="relative">
                  <select
                    name="roleId"
                    value={formData.roleId}
                    onChange={handleChange}
                    required
                    disabled={isLastActiveSuperAdmin}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none cursor-pointer text-xs text-slate-800 dark:text-slate-200 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    <option value="">Select Role</option>
                    {roles.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.roleName}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <KeyboardArrowDownIcon className="text-slate-400" sx={{ fontSize: 16 }} />
                  </div>
                </div>
                {isLastActiveSuperAdmin && (
                  <p className="text-amber-600 dark:text-amber-400 text-xs">
                    This is the only active Super Admin. Assign another Super Admin before changing this role.
                  </p>
                )}
              </div>

              <div className="space-y-1.5 min-w-0">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Email *</label>
                <div className="relative">
                  <EmailIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
                  <input
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    type="email"
                    required
                    placeholder="Enter email address"
                    className={`w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 ${
                      emailExistSameClinic ? "border-red-300 dark:border-red-500" : "border-gray-600 dark:border-slate-600"
                    }`}
                  />
                </div>
                {emailExistSameClinic && (
                  <p className="text-red-500 dark:text-red-400 text-xs">Email already exists in this clinic.</p>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Title *</label>
                <div className="relative">
                  <select
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none cursor-pointer text-xs text-slate-800 dark:text-slate-200"
                  >
                    <option value="">Select Title</option>
                    {patienttitles.map((item: { value: string; title: string }, i: number) => (
                      <option key={i} value={item.value}>
                        {item.title}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <KeyboardArrowDownIcon className="text-slate-400" sx={{ fontSize: 16 }} />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5 min-w-0">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">First Name *</label>
                <div className="relative">
                  <PersonIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
                  <input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    type="text"
                    required
                    className="w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200"
                  />
                </div>
              </div>

              <div className="space-y-1.5 min-w-0">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Last Name *</label>
                <div className="relative">
                  <PersonIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
                  <input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    type="text"
                    required
                    className="w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Phone Number *</label>
                <div className="relative [&_.PhoneInput]:bg-slate-50 dark:[&_.PhoneInput]:bg-slate-700 [&_.PhoneInput]:border [&_.PhoneInput]:border-gray-600 dark:[&_.PhoneInput]:border-slate-600 [&_.PhoneInput]:rounded-lg [&_.PhoneInput]:px-3 [&_.PhoneInput]:py-0 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:border-none [&_.PhoneInputInput]:text-xs [&_.PhoneInputInput]:text-slate-800 dark:[&_.PhoneInputInput]:text-slate-200">
                  <PhoneInput
                    international
                    countryCallingCodeEditable={false}
                    defaultCountry="LK"
                    value={formData.phoneNumber}
                    onChange={handlePhoneNumberChange}
                  />
                </div>
              </div>

              <div className="space-y-1.5 md:col-span-2">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Address *</label>
                <div className="relative">
                  <LocationOnIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
                  <input
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    type="text"
                    required
                    className="w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-gray-600 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="submit"
                disabled={savingBasic}
                className="px-4 py-2 rounded-lg bg-slate-900 dark:bg-primary text-white font-semibold text-xs disabled:opacity-50"
              >
                {savingBasic ? "Saving…" : "Update basic information"}
              </button>
            </div>
          </form>

          {/* Calendar Color */}
          <form
            onSubmit={handleUpdateColor}
            className="bg-white dark:bg-slate-800 rounded-[8px] shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-4 border border-slate-100 dark:border-slate-700"
          >
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <PaletteIcon className="text-primary" sx={{ fontSize: 16 }} />
              Calendar Color
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-xs mb-3">
              Choose a color to identify this employee in the calendar
            </p>
            <div className="flex flex-wrap gap-2">
              {colorOptions.map((c, index) => (
                <label key={index} className="relative cursor-pointer group">
                  <input
                    type="radio"
                    name="color"
                    value={c.color}
                    onChange={handleChange}
                    checked={formData.color === c.color}
                    className="sr-only"
                  />
                  <div
                    className={`w-10 h-10 rounded-lg border border-slate-300/80 shadow-inner transition-all duration-200 ${
                      formData.color === c.color ? "ring-2 ring-offset-2 ring-primary scale-110" : "hover:scale-105"
                    }`}
                    style={{ backgroundColor: c.color }}
                  />
                  <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] text-slate-500 dark:text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {c.name}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex justify-end mt-8">
              <button
                type="submit"
                disabled={savingColor}
                className="px-4 py-2 rounded-lg bg-slate-900 dark:bg-primary text-white font-semibold text-xs disabled:opacity-50"
              >
                {savingColor ? "Saving…" : "Update calendar color"}
              </button>
            </div>
          </form>

          {/* Professional Details */}
          <form
            onSubmit={handleUpdateProfessional}
            className="bg-white dark:bg-slate-800 rounded-[8px] shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-4 border border-slate-100 dark:border-slate-700"
          >
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <SchoolIcon className="text-primary" sx={{ fontSize: 16 }} />
              Professional Details
            </h2>
            {!formData.IsBookDentist ? (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Enable &quot;Online Booking Dentist&quot; to edit studies, specialities, and languages.
              </p>
            ) : (
              <>
                <div className="space-y-3">
                  <div className="space-y-1.5 professional-details-autocomplete">
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                      <SchoolIcon className="text-slate-400" sx={{ fontSize: 14 }} />
                      Studies & Qualifications
                    </label>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={formData.studies ?? []}
                      onChange={(_, newValue) => setFormData((prev) => ({ ...prev, studies: newValue }))}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={index}
                            label={option}
                            className="!bg-indigo-50 dark:!bg-indigo-900/40 !text-indigo-700 dark:!text-indigo-300 !border-indigo-200 dark:!border-indigo-700"
                            variant="outlined"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="e.g., BDS, MFDS RCS Eng"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgb(248 250 252)",
                              borderRadius: "0.75rem",
                              fontSize: "0.75rem",
                              minHeight: "2.5rem",
                              "& fieldset": { borderColor: "rgb(75 85 99)" },
                              "&.Mui-focused fieldset": { borderColor: "rgb(99 102 241)", borderWidth: "2px" },
                            },
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="space-y-1.5 professional-details-autocomplete">
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                      <MedicalServicesIcon className="text-slate-400" sx={{ fontSize: 14 }} />
                      Specialities
                    </label>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={formData.specialities ?? []}
                      onChange={(_, newValue) => setFormData((prev) => ({ ...prev, specialities: newValue }))}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={index}
                            label={option}
                            className="!bg-purple-50 dark:!bg-purple-900/40 !text-purple-700 dark:!text-purple-300 !border-purple-200 dark:!border-purple-700"
                            variant="outlined"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="e.g., Orthodontics"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgb(248 250 252)",
                              borderRadius: "0.75rem",
                              fontSize: "0.75rem",
                              "& fieldset": { borderColor: "rgb(75 85 99)" },
                              "&.Mui-focused fieldset": { borderColor: "rgb(168 85 247)", borderWidth: "2px" },
                            },
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                      <TranslateIcon className="text-slate-400" sx={{ fontSize: 14 }} />
                      Languages
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {languageOptions.map((lang) => (
                        <label
                          key={lang}
                          className={`px-3 py-1.5 rounded-lg border-2 cursor-pointer transition-all text-xs ${
                            formData.languages.includes(lang)
                              ? "bg-primary/10 dark:bg-primary/20 border-primary text-primary"
                              : "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-300"
                          }`}
                        >
                          <input
                            type="checkbox"
                            value={lang}
                            checked={formData.languages.includes(lang)}
                            onChange={() => handleLanguageToggle(lang)}
                            className="sr-only"
                          />
                          {lang}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <button
                    type="submit"
                    disabled={savingProf}
                    className="px-4 py-2 rounded-lg bg-slate-900 dark:bg-primary text-white font-semibold text-xs disabled:opacity-50"
                  >
                    {savingProf ? "Saving…" : "Update professional details"}
                  </button>
                </div>
              </>
            )}
          </form>

          {/* Employee Type */}
          <form
            onSubmit={handleUpdateEmployeeType}
            className="bg-white dark:bg-slate-800 rounded-[8px] shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 p-4 border border-slate-100 dark:border-slate-700"
          >
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <ManageAccountsIcon className="text-primary" sx={{ fontSize: 16 }} />
              Employee Type
            </h2>
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center gap-2.5 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                <div className="relative">
                  <input
                    type="checkbox"
                    name="IsDentist"
                    checked={formData.IsDentist}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-slate-300 dark:bg-slate-600 rounded-full peer-checked:bg-primary transition-colors" />
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white dark:bg-slate-200 rounded-full shadow-sm transition-transform peer-checked:translate-x-4" />
                </div>
                <span className="font-medium text-xs text-slate-700 dark:text-slate-200">Dentist</span>
              </label>

              <label className="flex items-center gap-2.5 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                <div className="relative">
                  <input
                    type="checkbox"
                    name="IsBookDentist"
                    checked={formData.IsBookDentist}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-slate-300 dark:bg-slate-600 rounded-full peer-checked:bg-primary transition-colors" />
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white dark:bg-slate-200 rounded-full shadow-sm transition-transform peer-checked:translate-x-4" />
                </div>
                <span className="font-medium text-xs text-slate-700 dark:text-slate-200">Online Booking Dentist</span>
              </label>
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="submit"
                disabled={savingType}
                className="px-4 py-2 rounded-lg bg-slate-900 dark:bg-primary text-white font-semibold text-xs disabled:opacity-50"
              >
                {savingType ? "Saving…" : "Update employee type"}
              </button>
            </div>
          </form>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate("/client/staff/employee/all-employees")}
              className="px-6 py-2.5 rounded-lg border-2 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-all text-xs"
            >
              Back to list
            </button>
          </div>
        </div>

        {roleName === "Super Admin" && (
          <div className="bg-white rounded-[8px] shadow-xl shadow-slate-200/50 p-4 border border-slate-100 mt-3">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <LockIcon className="text-primary" sx={{ fontSize: 16 }} />
              Update Employee Password
            </h2>
            <form onSubmit={handleUpdatePassword} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Your Password *</label>
                  <div className="relative">
                    <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
                    <input
                      name="currentUserPassword"
                      value={currentUserPassword}
                      onChange={handleUpdatePasswordChange}
                      type={showCurrentPassword ? "text" : "password"}
                      required
                      className="w-full pl-10 pr-10 py-2 bg-slate-50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none text-xs"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      {showCurrentPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">New Password *</label>
                  <div className="relative">
                    <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
                    <input
                      name="updatePassword"
                      value={updatePassword}
                      onChange={handleUpdatePasswordChange}
                      type={showUpdatePassword ? "text" : "password"}
                      required
                      className="w-full pl-10 pr-10 py-2 bg-slate-50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none text-xs"
                    />
                    <button
                      type="button"
                      onClick={() => setShowUpdatePassword(!showUpdatePassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      {showUpdatePassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                    </button>
                  </div>
                  {updatePwdLength && (
                    <p className="text-amber-500 text-xs">Password must be at least 8 characters</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Confirm New Password *</label>
                  <div className="relative">
                    <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
                    <input
                      name="updateConfirmPassword"
                      value={updateConfirmPassword}
                      onChange={handleUpdatePasswordChange}
                      type={showUpdateConfirmPassword ? "text" : "password"}
                      required
                      className="w-full pl-10 pr-10 py-2 bg-slate-50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-primary/20 outline-none text-xs"
                    />
                    <button
                      type="button"
                      onClick={() => setShowUpdateConfirmPassword(!showUpdateConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      {showUpdateConfirmPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                    </button>
                  </div>
                  {updatePasswordNotMatch && (
                    <p className="text-red-500 text-xs">Passwords do not match</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-semibold shadow-lg text-xs flex items-center gap-2"
                >
                  <LockIcon sx={{ fontSize: 16 }} />
                  Update Password
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditEmployee;
